/**
 * §6.3 角色與權限。需要 `role.write`。
 *
 * ── 這一頁最重要的觀念 ────────────────────────────────────────────────────
 * **權限是程式碼，角色是資料。**（§6.1）
 * 畫面上只能把既有的權限組合成角色，不能發明新權限——新權限要改程式，
 * 因為每一個權限都得有人在後端的路由上宣告它。所以這裡沒有「新增權限」。
 *
 * 內建的六個角色**不能編輯**（§6.3）。要不一樣的組合就複製一份成自訂角色再改。
 * 理由是內建角色的定義在程式碼裡，每次服務啟動都會同步回去——在畫面上改了
 * 也會被蓋掉，那比直接擋住更難理解。
 */

import { useMemo, useState } from 'react'
import { ApiError, api } from '../../api/client.js'
import { useQuery } from '../../api/hooks.js'
import { Alert, Button, Card, Empty, Field, Mono, Pill, Table, Td, TextInput } from '../../components/ui.js'
import { count } from '../../../../shared/lib/format.js'
import {
  CATEGORY_LABELS,
  PERMISSIONS,
  type PermissionCategory,
  type PermissionDef,
} from '../../../../shared/permissions.js'

interface RoleRow {
  id: number
  code: string
  name: string
  description: string | null
  is_builtin: boolean
  all_sites_only: boolean
  permissions: string[]
  userCount: number
}

const CATEGORY_ORDER: PermissionCategory[] = ['ops', 'query', 'settings', 'system']

export function AdminRoles() {
  const list = useQuery<{ items: RoleRow[] }>('/api/admin/roles')
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; role?: RoleRow } | null>(null)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null)
  const [busy, setBusy] = useState<number | null>(null)

  const remove = async (r: RoleRow) => {
    if (!confirm(`確定要刪除角色「${r.name}」嗎？\n\n目前沒有任何帳號在使用它，所以刪掉不會影響任何人。\n刪除這件事會記入認證稽核。`)) return
    setBusy(r.id)
    try {
      await api.del(`/api/admin/roles/${r.id}`)
      setNotice({ tone: 'ok', text: `角色「${r.name}」已刪除。` })
      list.reload()
    } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof ApiError ? e.message : String(e) })
    } finally { setBusy(null) }
  }

  const roles = list.data?.items ?? []
  const builtin = roles.filter((r) => r.is_builtin)
  const custom = roles.filter((r) => !r.is_builtin)

  const done = (text: string) => {
    setNotice({ tone: 'ok', text })
    setEditing(null)
    list.reload()
  }

  return (
    <div className="space-y-4">
      {notice && <Alert tone={notice.tone === 'ok' ? 'ok' : 'danger'}>{notice.text}</Alert>}

      <Card
        title="自訂角色"
        subtitle="把既有的權限組合起來。權限本身是程式碼定義的，畫面上不能新增"
        right={<Button size="sm" variant="primary" onClick={() => setEditing({ mode: 'create' })}>建立角色</Button>}
      >
        {list.error && <Alert tone="danger" title="讀不到角色">{list.error.message}</Alert>}
        {custom.length === 0 && !list.loading && (
          <Empty>還沒有自訂角色。內建的六個通常就夠用，不夠再建。</Empty>
        )}
        {custom.length > 0 && (
          <Table head={['代碼', '名稱', '權限', '使用中', '']}>
            {custom.map((r) => (
              <tr key={r.id}>
                <Td><Mono>{r.code}</Mono></Td>
                <Td>
                  <span className="text-sm">{r.name}</span>
                  {r.description && <span className="block text-[11px] text-ink-500">{r.description}</span>}
                </Td>
                <Td className="text-xs"><PermSummary codes={r.permissions} /></Td>
                <Td className="text-xs">
                  {r.userCount > 0
                    ? `${count(r.userCount)} 人`
                    : <span className="text-ink-300">沒有人用</span>}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" onClick={() => setEditing({ mode: 'edit', role: r })}>編輯</Button>
                    {/* §6.3「不能刪除仍有帳號使用中的角色」——有人用就不給刪，
                        而且直接說明要先做什麼，不要只把按鈕變灰 */}
                    {r.userCount === 0 ? (
                      <Button size="sm" variant="danger" disabled={busy === r.id}
                        onClick={() => void remove(r)}>
                        {busy === r.id ? '刪除中…' : '刪除'}
                      </Button>
                    ) : (
                      <span className="text-[11px] text-ink-400"
                        title={`還有 ${r.userCount} 個帳號在用。請先到帳號管理把他們改成別的角色`}>
                        使用中，不可刪
                      </span>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="內建角色" subtitle="§6.3 不可編輯——定義在程式碼裡，每次服務啟動都會同步回去">
        <Table head={['代碼', '名稱', '說明', '權限', '使用中', '']}>
          {builtin.map((r) => (
            <tr key={r.id}>
              <Td><Mono>{r.code}</Mono></Td>
              <Td className="text-sm">
                {r.name}
                {r.all_sites_only && <Pill tone="violet" title="只能以「全廠區」授予">全廠區</Pill>}
              </Td>
              <Td className="text-xs text-ink-500 max-w-[280px]">{r.description}</Td>
              <Td className="text-xs"><PermSummary codes={r.permissions} /></Td>
              <Td className="text-xs">
                {r.userCount > 0 ? `${count(r.userCount)} 人` : <span className="text-ink-300">—</span>}
              </Td>
              <Td>
                <Button size="sm"
                  onClick={() => setEditing({
                    mode: 'create',
                    role: { ...r, code: '', name: `${r.name}（自訂）`, id: 0, is_builtin: false },
                  })}>複製一份來改</Button>
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      {editing && (
        <RoleForm
          mode={editing.mode}
          role={editing.role}
          existingCodes={roles.map((r) => r.code)}
          onClose={() => setEditing(null)}
          onDone={done}
          onError={(text) => setNotice({ tone: 'danger', text })}
        />
      )}
    </div>
  )
}

/** 用分類計數代替列出四十幾個代碼——那個沒有人看得完。 */
function PermSummary({ codes }: { codes: string[] }) {
  const byCat = useMemo(() => {
    const m = new Map<PermissionCategory, number>()
    for (const c of codes) {
      const def = PERMISSIONS.find((p) => p.code === c)
      if (def) m.set(def.category, (m.get(def.category) ?? 0) + 1)
    }
    return m
  }, [codes])

  const highRisk = codes.filter((c) => PERMISSIONS.find((p) => p.code === c)?.highRisk)

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {CATEGORY_ORDER.filter((c) => byCat.has(c)).map((c) => (
        <span key={c} className="text-[11px] text-ink-600">
          {CATEGORY_LABELS[c]} {byCat.get(c)}
        </span>
      ))}
      {codes.length === 0 && <span className="text-ink-300">無</span>}
      {highRisk.length > 0 && (
        <Pill tone="red" title={highRisk.join('、')}>高風險 {highRisk.length}</Pill>
      )}
    </span>
  )
}

// ── 建立／編輯 ──────────────────────────────────────────────────────────────

function RoleForm({ mode, role, existingCodes, onClose, onDone, onError }: {
  mode: 'create' | 'edit'
  role?: RoleRow
  existingCodes: string[]
  onClose: () => void
  onDone: (text: string) => void
  onError: (text: string) => void
}) {
  const [code, setCode] = useState(role?.code ?? '')
  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [picked, setPicked] = useState<Set<string>>(new Set(role?.permissions ?? []))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggle = (c: string) => setPicked((s) => {
    const next = new Set(s)
    if (next.has(c)) next.delete(c); else next.add(c)
    return next
  })

  const codeOk = mode === 'edit' || /^[a-z][a-z0-9_]{1,39}$/.test(code)
  const codeTaken = mode === 'create' && existingCodes.includes(code)
  const highRiskPicked = [...picked]
    .map((c) => PERMISSIONS.find((p) => p.code === c))
    .filter((p): p is PermissionDef => Boolean(p?.highRisk))

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const permissions = [...picked]
      if (mode === 'create') {
        await api.post('/api/admin/roles', {
          code: code.trim(), name: name.trim(),
          description: description.trim() || null, permissions,
        })
        onDone(`角色「${name}」已建立。`)
      } else {
        await api.patch(`/api/admin/roles/${role!.id}`, {
          name: name.trim(), description: description.trim() || null, permissions,
        })
        onDone(`角色「${name}」已更新。${role!.userCount > 0
          ? `${role!.userCount} 個使用中的帳號會在下次登入時套用新權限。` : ''}`)
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e)
      setError(msg)
      onError(msg)
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4">
      <div className="w-full max-w-3xl rounded-lg border border-ink-200 bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="font-semibold text-ink-900">
            {mode === 'create' ? '建立自訂角色' : `編輯 ${role?.code}`}
          </h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700" aria-label="關閉">✕</button>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="角色代碼" hint={mode === 'create' ? '小寫英數與底線，建立後不可修改' : '不可修改'} required>
            <TextInput value={code} disabled={mode === 'edit'} className="font-mono"
              placeholder="line_lead" onChange={(e) => setCode(e.target.value.toLowerCase())} />
            {code && !codeOk && <p className="mt-1 text-xs text-red-700">只能用小寫英文、數字、底線，且要以英文開頭。</p>}
            {codeTaken && <p className="mt-1 text-xs text-red-700">這個代碼已經有人用了。</p>}
          </Field>
          <Field label="名稱" hint="畫面上顯示的文字" required>
            <TextInput value={name} placeholder="線長" onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="說明" hint="指派角色時會看到這一句">
            <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>

        {role && role.userCount > 0 && mode === 'edit' && (
          <div className="mt-3">
            <Alert tone="warn" title={`目前有 ${count(role.userCount)} 個帳號用這個角色`}>
              權限變更會影響他們。<strong>已經登入的人要重新登入才會套用</strong>，
              不會在他們作業到一半時突然變。
            </Alert>
          </div>
        )}

        {/* §6.1：高風險權限勾選時要額外顯示警告 */}
        {highRiskPicked.length > 0 && (
          <div className="mt-3">
            <Alert tone="danger" title={`這個角色含有 ${highRiskPicked.length} 個高風險權限`}>
              <ul className="ml-4 list-disc space-y-1">
                {highRiskPicked.map((p) => (
                  <li key={p.code}><strong>{p.label}</strong>——{p.description}</li>
                ))}
              </ul>
            </Alert>
          </div>
        )}

        <div className="mt-4 flex items-baseline justify-between">
          <p className="text-sm font-medium text-ink-800">權限（已選 {picked.size} 項）</p>
          <p className="text-xs text-ink-400">權限是程式碼定義的固定清單，不能在這裡新增</p>
        </div>

        <div className="mt-2 max-h-[22rem] space-y-3 overflow-y-auto rounded-md border border-ink-200 p-3">
          {CATEGORY_ORDER.map((cat) => {
            const defs = PERMISSIONS.filter((p) => p.category === cat)
            const allOn = defs.every((d) => picked.has(d.code))
            return (
              <div key={cat}>
                <div className="mb-1 flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
                    {CATEGORY_LABELS[cat]}
                  </h3>
                  <Button size="sm" variant="ghost" onClick={() => setPicked((s) => {
                    const next = new Set(s)
                    for (const d of defs) { if (allOn) next.delete(d.code); else next.add(d.code) }
                    return next
                  })}>{allOn ? '全部取消' : '全選'}</Button>
                </div>
                <div className="space-y-1">
                  {defs.map((d) => (
                    <label key={d.code}
                      className={`flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm ${
                        picked.has(d.code) ? 'bg-sky-50' : 'hover:bg-ink-50'}`}>
                      <input type="checkbox" className="mt-1 h-4 w-4 shrink-0"
                        checked={picked.has(d.code)} onChange={() => toggle(d.code)} />
                      <span className="min-w-0">
                        <span className="font-medium text-ink-900">{d.label}</span>
                        {d.highRisk && <Pill tone="red">高風險</Pill>}
                        <Mono className="ml-1.5 text-[11px] text-ink-400">{d.code}</Mono>
                        {/* §6.3：每個權限旁邊要有一句說明 */}
                        <span className="block text-xs text-ink-500">{d.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        {picked.size === 0 && (
          <p className="mt-2 text-xs text-amber-800">
            一個權限都沒選的話，用這個角色的人登入後什麼都做不了。
          </p>
        )}

        {error && <div className="mt-3"><Alert tone="danger">{error}</Alert></div>}

        <div className="mt-4 flex gap-2">
          <Button variant="primary" className="flex-1" disabled={busy || !codeOk || codeTaken || !name.trim()}
            onClick={() => void submit()}>
            {busy ? '請稍候…' : mode === 'create' ? '建立角色' : '儲存'}
          </Button>
          <Button onClick={onClose} disabled={busy}>取消</Button>
        </div>
      </div>
    </div>
  )
}
